import { useEffect, useState } from 'react';
import { Plus, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { usePageTitle } from '@/hooks/usePageTitle';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { TableSkeleton } from '@/components/ui/skeleton';
import { useReleaseNoteStore } from '../store';
import { ReleaseNotesAdminTable, ReleaseFormDialog } from '../components';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import type { ReleaseFormData } from '../schemas';

export function ReleaseNotesAdminPage() {
  usePageTitle('Manage Release Notes');

  const {
    adminReleases,
    isLoadingAdmin,
    adminError,
    selectedRelease,
    isSubmitting,
    fetchAdminReleases,
    fetchReleaseDetail,
    createRelease,
    updateRelease,
    deleteRelease,
    clearSelectedRelease,
  } = useReleaseNoteStore();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  useEffect(() => {
    fetchAdminReleases();
  }, [fetchAdminReleases]);

  const handleCreate = () => {
    setEditingId(null);
    clearSelectedRelease();
    setDialogOpen(true);
  };

  const handleEdit = async (id: number) => {
    setEditingId(id);
    await fetchReleaseDetail(id);
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this release?')) return;

    try {
      await deleteRelease(id);
      toast.success('Release deleted successfully.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete release'));
    }
  };

  const handleSubmit = async (data: ReleaseFormData) => {
    try {
      const payload = {
        ...data,
        notes: data.notes.map((n, i) => ({ ...n, sortOrder: i })),
        highlights: data.highlights.map((h, i) => ({ ...h, sortOrder: i })),
      };
      if (editingId) {
        await updateRelease(editingId, payload);
        toast.success('Release updated successfully.');
      } else {
        await createRelease(payload);
        toast.success('Release created successfully.');
      }
      setDialogOpen(false);
      setEditingId(null);
      clearSelectedRelease();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save release'));
    }
  };

  const handleDialogChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEditingId(null);
      clearSelectedRelease();
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Megaphone className="h-5 w-5" />
            Release Notes
          </h1>
        }
        description={'Manage release notes visible to all users on the "What\'s New" page.'}
        actions={
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            New Release
          </Button>
        }
      />

      {isLoadingAdmin ? (
        <TableSkeleton rows={8} cols={6} />
      ) : adminError ? (
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
          <p className="text-destructive">{adminError}</p>
          <button
            onClick={() => fetchAdminReleases()}
            className="text-sm text-primary hover:underline"
          >
            Try again
          </button>
        </div>
      ) : (
        <ReleaseNotesAdminTable
          data={adminReleases}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <ReleaseFormDialog
        open={dialogOpen}
        onOpenChange={handleDialogChange}
        onSubmit={handleSubmit}
        release={editingId ? selectedRelease : null}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
